import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { UI_BOTTOM_COMPONENTS } from '~/constants/variables';
import { useGlobal } from '~/hooks/useGlobal';

import { mainStore } from '~/stores';

import { ALERT_DURATION } from '../../stores/constants';

import styles from './Alert.module.scss';

export default observer(() => {
  const { isSSR, isMobile } = useGlobal();
  const [bottomOffset, setBottomOffset] = useState(10);

  const currentAlert = mainStore?.alerts?.slice(-1)[0];

  const handleDismiss = () => {
    const alert = mainStore.popAlert();
    if (alert && alert.onDismiss) {
      alert.onDismiss();
    }
  };

  useEffect(() => {
    if (!mainStore.alerts.length) {
      return;
    }
    const timeout = setTimeout(() => {
      mainStore.popAlert();
    }, currentAlert?.duration || ALERT_DURATION);
    return () => clearTimeout(timeout);
  });

  const updateOffset = () => {
    let totalOffset = 10;

    const tryUpdate = () => {
      const navbar = document.querySelector(`#${UI_BOTTOM_COMPONENTS.NAVBAR}`);
      const cta = document.querySelector(`#${UI_BOTTOM_COMPONENTS.CTA}`);

      if (navbar) {
        totalOffset += navbar.getBoundingClientRect().height;
      }
      if (cta) {
        totalOffset += cta.getBoundingClientRect().height;
      }

      const cssOffset = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--offset-bottom',
        ) || '0',
        10,
      );

      totalOffset += cssOffset;
      setBottomOffset(totalOffset);
    };

    tryUpdate();
  };

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    const observer = new MutationObserver(() => {
      requestAnimationFrame(updateOffset);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    window.addEventListener('resize', updateOffset);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateOffset);
    };
  }, [isMobile]);

  if (!mainStore.alerts.length || isSSR) {
    return null;
  }

  if (!currentAlert) {
    return;
  }

  return createPortal(
    <>
      <div
        className={classes([
          `alert _fixed _${currentAlert.type}`,
          currentAlert.className,
          currentAlert.action && styles.withAction,
        ])}
        key={currentAlert.id}
        style={isMobile ? { bottom: `${bottomOffset}px` } : {}}
      >
        <div
          className={classes([
            styles.message,
            currentAlert.action && styles.messageWithAction,
          ])}
          onClick={handleDismiss}
          dangerouslySetInnerHTML={{ __html: currentAlert.message }}
        />
        <div>
          {currentAlert.action && (
            <button
              className="button _primary _small"
              onClick={() => {
                currentAlert.action?.onClick();
                handleDismiss();
              }}
            >
              {currentAlert.action.label}
            </button>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
});
